import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
} from '@mui/material';
import {
  Sensors,
  People,
  AccessTime,
  Wifi,
  WbSunny,
  Analytics,
  Notifications,
  Settings,
} from '@mui/icons-material';
import ModernButton, { GradientButton, GlassButton } from '../common/ModernButton';
import { useLanguage } from '../../contexts/LanguageContext';

const WidgetSelector = ({ open, onClose, onAddWidget, existingWidgets = [] }) => {
  const { translate } = useLanguage();
  const [selectedWidget, setSelectedWidget] = useState(null);

  const availableWidgets = [
    {
      id: 'room-sensors',
      title: translate('roomSensors'),
      description: translate('roomSensorsDescription'),
      icon: <Sensors />,
      category: 'monitoring',
    },
    {
      id: 'employees',
      title: translate('employees'),
      description: translate('employeesDescription'),
      icon: <People />,
      category: 'management',
    },
    {
      id: 'access-logs',
      title: translate('accessLogs'),
      description: translate('accessLogsDescription'),
      icon: <AccessTime />,
      category: 'security',
    },
    {
      id: 'unifi',
      title: translate('roomInformation'),
      description: translate('roomInformationDescription'),
      icon: <Wifi />,
      category: 'monitoring',
    },
    {
      id: 'weather',
      title: translate('weatherAndTime'),
      description: translate('weatherDescription'),
      icon: <WbSunny />,
      category: 'external',
    },
    {
      id: 'analytics',
      title: translate('generalAnalysis'),
      description: translate('analyticsDescription'),
      icon: <Analytics />,
      category: 'analytics',
    },
    {
      id: 'notifications',
      title: translate('notifications'),
      description: translate('notificationsDescription'),
      icon: <Notifications />,
      category: 'system',
    },
    {
      id: 'settings',
      title: translate('settings'),
      description: translate('settingsDescription'),
      icon: <Settings />,
      category: 'system',
    },
  ];

  const handleWidgetSelect = (widget) => {
    setSelectedWidget(widget);
  };

  const handleAddWidget = () => {
    if (selectedWidget) {
      onAddWidget(selectedWidget);
      setSelectedWidget(null);
      onClose();
    }
  };

  const isWidgetAlreadyAdded = (widgetId) => {
    return existingWidgets.some(widget => widget.type === widgetId);
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'monitoring':
        return 'primary';
      case 'management':
        return 'success';
      case 'security':
        return 'warning';
      case 'analytics':
        return 'info';
      case 'system':
        return 'secondary';
      case 'external':
        return 'default';
      default:
        return 'default';
    }
  };

  const filteredWidgets = availableWidgets.filter(widget => !isWidgetAlreadyAdded(widget.id));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
        {translate('selectWidget')}
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate('selectWidgetDescription')}
        </Typography>

        {filteredWidgets.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {translate('allWidgetsAdded')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredWidgets.map((widget) => (
              <Grid item xs={12} sm={6} key={widget.id}>
                <Box
                  sx={{
                    border: `2px solid ${selectedWidget?.id === widget.id ? '#1976d2' : 'rgba(0,0,0,0.1)'}`,
                    borderRadius: 2,
                    p: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: selectedWidget?.id === widget.id ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                    '&:hover': {
                      borderColor: '#1976d2',
                      background: 'rgba(25, 118, 210, 0.05)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                  onClick={() => handleWidgetSelect(widget)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        mr: 2,
                      }}
                    >
                      {widget.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {widget.title}
                      </Typography>
                      <Chip
                        label={widget.category}
                        color={getCategoryColor(widget.category)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    {widget.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <GlassButton onClick={onClose}>
          {translate('cancel')}
        </GlassButton>
        <GradientButton
          onClick={handleAddWidget}
          disabled={!selectedWidget}
          startIcon={<Settings />}
        >
          {translate('addWidget')}
        </GradientButton>
      </DialogActions>
    </Dialog>
  );
};

export default WidgetSelector; 